import { Row, Container } from '@everlywell/leaves';
import PostCard from 'components/PostCard';
import React, { useState, useEffect } from 'react';
import { generateKey } from 'utils/helpers';
import { IResource, TResourceType, TResourceSort } from 'utils/types';

import ResourceListFilter from './ResourceListFilter';
import ResourceListSort from './ResourceListSort';
import * as S from './styles';

export interface IResourceListSection {
  slug: string;
  resources: IResource[];
}

type TFilterOption = 'all' | TResourceType;

const ResourceListSection: React.FC<IResourceListSection> = ({
  slug,
  resources,
}: IResourceListSection) => {
  const [filterOptions, setFilterOptions] = useState<TFilterOption[]>([]);
  const [activeFilter, setActiveFilter] = useState<TFilterOption>('all');

  const [sortOptions, setSortOptions] = useState<TResourceSort[]>([]);
  const [activeSort, setActiveSort] = useState<TResourceSort>('most recent');

  const [filteredResources, setFilteredResources] =
    useState<IResource[]>(resources);

  const handleFilterSelect = (filterIndex: number) => {
    setActiveFilter(filterOptions[filterIndex]);
  };

  const handleSortSelect = (sortIndex: number) => {
    setActiveSort(sortOptions[sortIndex]);
  };

  useEffect(() => {
    const uniqueOptions: TFilterOption[] = ['all'];
    resources.forEach((i) => {
      if (uniqueOptions.indexOf(i.type) < 0) {
        uniqueOptions.push(i.type);
      }
    });
    setFilterOptions(uniqueOptions);
  }, []);

  useEffect(() => {
    const uniqueSortOptions: TResourceSort[] = ['most recent', 'oldest'];
    setSortOptions(uniqueSortOptions);
  }, []);

  useEffect(() => {
    const allResources = [...resources];
    const sortedResources = allResources.sort(
      (a, b) => (a.updatedAt < b.updatedAt ? -1 : 1), // sorting by oldest to newest
    );
    activeSort === 'most recent'
      ? setFilteredResources(sortedResources.reverse()) // sorting by newest to oldest
      : setFilteredResources(sortedResources);

    activeFilter === 'all'
      ? setFilteredResources(allResources)
      : setFilteredResources(
          allResources.filter((x) => x.type === activeFilter),
        );
  }, [activeSort, activeFilter]);

  return (
    <S.StyledSectionWrapper data-testid="resourceListSection" id={slug}>
      <Container>
        <Row bottom="md">
          <S.ResourceListFilterCol xs={12} md={7}>
            <ResourceListFilter
              onFilterSelect={handleFilterSelect}
              filters={filterOptions}
              initialActiveIndex={filterOptions.indexOf(activeFilter)}
            />
          </S.ResourceListFilterCol>
          <S.ResourceListFilterCol xs={12} md={3} mdOffset={2}>
            <ResourceListSort
              onSortSelect={handleSortSelect}
              sorts={sortOptions}
              activeSortIndex={sortOptions.indexOf(activeSort)}
            />
          </S.ResourceListFilterCol>
        </Row>
        {filteredResources?.length
          ? filteredResources.map((resource) => (
              <PostCard resource={resource} key={generateKey()} />
            ))
          : null}
      </Container>
    </S.StyledSectionWrapper>
  );
};

export default ResourceListSection;
